/**
 * ATW product marks: line symbols in the logo's diamond language (navy + #4A6CF7).
 * Files live in `public/images/atw-marks/`.
 */

export type AtwMarkId =
  | "directory"
  | "one"
  | "mail"
  | "workspaces"
  | "hub"
  | "pay"
  | "agents"
  | "worksuite"
  | "platform"
  | "crm"
  | "finance"
  | "hr"
  | "marketing"
  | "knowledge"
  | "projects"
  | "analytics"
  | "support"
  | "custom-apps"
  | "mail-calendar"
  | "documents"
  | "meetings"
  | "messaging";

export type AtwMark = {
  id: AtwMarkId;
  name: string;
  kind: "system" | "app";
  /** Parent system when this is an application tile. */
  system?: "one" | "worksuite";
  src: string;
  color: string;
};

export const ATW_MARKS: AtwMark[] = [
  { id: "directory", name: "Directory", kind: "system", src: "/images/atw-marks/system-directory.svg", color: "#1e4b96" },
  { id: "one", name: "One", kind: "system", src: "/images/atw-marks/system-one.svg", color: "#1e4b96" },
  { id: "mail", name: "Mail", kind: "system", src: "/images/atw-marks/system-mail.svg", color: "#1e4b96" },
  {
    id: "workspaces",
    name: "WorkSuite",
    kind: "system",
    src: "/images/atw-marks/system-mail.svg",
    color: "#1e4b96",
  },
  { id: "hub", name: "Hub", kind: "system", src: "/images/atw-marks/system-hub.svg", color: "#1e4b96" },
  { id: "pay", name: "Pay", kind: "system", src: "/images/atw-marks/system-pay.svg", color: "#1e4b96" },
  { id: "agents", name: "Agents", kind: "system", src: "/images/atw-marks/system-agents.svg", color: "#1e4b96" },
  { id: "worksuite", name: "WorkSuite", kind: "system", src: "/images/atw-marks/system-worksuite.svg", color: "#1e4b96" },
  { id: "platform", name: "Platform", kind: "system", src: "/images/atw-marks/system-platform.svg", color: "#1e4b96" },
  { id: "crm", name: "CRM", kind: "app", system: "one", src: "/images/atw-marks/app-crm.svg", color: "#1e4b96" },
  { id: "finance", name: "Finance", kind: "app", system: "one", src: "/images/atw-marks/app-finance.svg", color: "#1e4b96" },
  { id: "hr", name: "HR", kind: "app", system: "one", src: "/images/atw-marks/app-hr.svg", color: "#1e4b96" },
  { id: "marketing", name: "Marketing", kind: "app", system: "one", src: "/images/atw-marks/app-marketing.svg", color: "#1e4b96" },
  { id: "knowledge", name: "Knowledge", kind: "app", system: "one", src: "/images/atw-marks/app-knowledge.svg", color: "#1e4b96" },
  { id: "projects", name: "Projects", kind: "app", system: "one", src: "/images/atw-marks/app-projects.svg", color: "#1e4b96" },
  { id: "analytics", name: "Analytics", kind: "app", system: "one", src: "/images/atw-marks/app-analytics.svg", color: "#1e4b96" },
  { id: "support", name: "Support", kind: "app", system: "one", src: "/images/atw-marks/app-support.svg", color: "#1e4b96" },
  { id: "custom-apps", name: "Custom apps", kind: "app", system: "one", src: "/images/atw-marks/app-custom.svg", color: "#1e4b96" },
  { id: "mail-calendar", name: "Mail & calendar", kind: "app", system: "worksuite", src: "/images/atw-marks/app-mail-calendar.svg", color: "#1e4b96" },
  { id: "documents", name: "Documents", kind: "app", system: "worksuite", src: "/images/atw-marks/app-documents.svg", color: "#1e4b96" },
  { id: "meetings", name: "Meetings", kind: "app", system: "worksuite", src: "/images/atw-marks/app-meetings.svg", color: "#1e4b96" },
  { id: "messaging", name: "Messaging", kind: "app", system: "worksuite", src: "/images/atw-marks/app-messaging.svg", color: "#1e4b96" },
];

const BY_ID = new Map(ATW_MARKS.map((mark) => [mark.id, mark]));

export function atwMark(id: AtwMarkId): AtwMark {
  const mark = BY_ID.get(id);
  if (!mark) throw new Error(`Unknown ATW mark: ${id}`);
  return mark;
}

export function isAtwMarkId(id: string): id is AtwMarkId {
  return BY_ID.has(id as AtwMarkId);
}

/** System id (`one`, `workspaces`, …) → product mark, when one exists. */
export function atwMarkIdForSystem(systemId: string): AtwMarkId | null {
  const id =
    systemId === "mail" || systemId === "workspaces" ? "worksuite" : systemId;
  return isAtwMarkId(id) && atwMark(id).kind === "system" ? id : null;
}

/**
 * Dashboard app slug → product mark.
 * `human-resources` is stored as mark id `hr`.
 */
export function atwMarkIdForAppSlug(slug: string): AtwMarkId | null {
  if (slug === "human-resources") return "hr";
  if (slug === "custom-apps") return "custom-apps";
  return isAtwMarkId(slug) && atwMark(slug).kind === "app" ? slug : null;
}
